import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  User, 
  ArrowRight, 
  ExternalLink,
  MessageCircle,
  Calendar,
  Layers,
  Filter,
  RefreshCw,
  Search,
  Trash2,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Image as ImageIcon
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { db, auth } from '../firebase';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { ShareFeedback, MonthlyPlan, ContentIdea } from '../types';
import { formatDistanceToNow, format } from 'date-fns';
import axios from 'axios';

interface ReviewFeedbackViewProps {
  brandId: string;
  onViewInCalendar: (planId: string, ideaId?: string | null) => void;
}

export const ReviewFeedbackView: React.FC<ReviewFeedbackViewProps> = ({ brandId, onViewInCalendar }) => {
  const [feedback, setFeedback] = useState<ShareFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'approved' | 'rejected' | 'comment'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [brandPlans, setBrandPlans] = useState<MonthlyPlan[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedPreviews, setExpandedPreviews] = useState<Record<string, boolean>>({});

  const togglePreview = (id: string) => {
    setExpandedPreviews(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getPostIdea = (planId: string, ideaId: string | null): ContentIdea | null => {
    if (!ideaId) return null;
    const plan = brandPlans.find(p => p.id === planId);
    if (!plan) return null;
    
    let calendar = plan.calendar;
    if (typeof calendar === 'string') {
      try { calendar = JSON.parse(calendar); } catch { calendar = []; }
    }
    const idea = (calendar as any[]).find(i => String(i.id) === String(ideaId));
    return (idea as ContentIdea) || null;
  };

  const getPlanIdeas = (planId: string): ContentIdea[] => {
    const plan = brandPlans.find(p => p.id === planId);
    if (!plan) return [];
    
    let calendar = plan.calendar;
    if (typeof calendar === 'string') {
      try { calendar = JSON.parse(calendar); } catch { calendar = []; }
    }
    return (calendar as ContentIdea[]) || [];
  };

  useEffect(() => {
    if (!brandId) return;

    setLoading(true);
    // Fetch feedback
    const q = query(
      collection(db, 'share_feedback'),
      where('brandId', '==', brandId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items: ShareFeedback[] = [];
      snapshot.forEach(doc => {
        items.push({ id: doc.id, ...doc.data() } as ShareFeedback);
      });
      // Sort client-side to avoid composite index requirement
      items.sort((a, b) => {
        const dateA = (a.createdAt as any).seconds ? (a.createdAt as any).seconds * 1000 : new Date(a.createdAt).getTime();
        const dateB = (b.createdAt as any).seconds ? (b.createdAt as any).seconds * 1000 : new Date(b.createdAt).getTime();
        return dateB - dateA;
      });
      setFeedback(items);
      setLoading(false);
    }, (err) => {
      console.warn("Share feedback listener error:", err);
      setLoading(false);
    });

    // Fetch plans to find post details
    const plansQ = query(
      collection(db, 'plans'),
      where('brandId', '==', brandId)
    );
    const plansUnsubscribe = onSnapshot(plansQ, (snapshot) => {
      const items: MonthlyPlan[] = [];
      snapshot.forEach(doc => {
        items.push({ id: doc.id, ...doc.data() } as MonthlyPlan);
      });
      setBrandPlans(items);
    }, (err) => {
      console.warn("Plans listener error:", err);
    });

    return () => {
      unsubscribe();
      plansUnsubscribe();
    };
  }, [brandId]);

  const handleDeleteFeedback = async (id: string) => {
    if (!window.confirm("Are you sure you want to clear this feedback? This will remove the approval/rejection status from the post.")) return;
    
    setDeletingId(id);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      await axios.delete(`/api/share/feedback/${id}`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
    } catch (err) {
      console.error("Failed to delete feedback", err);
      alert("Failed to delete feedback");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredFeedback = feedback.filter(f => {
    const matchesFilter = filter === 'all' || f.status === filter;
    const matchesSearch = searchTerm === '' || 
      f.reviewerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.feedback.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getPostTitle = (planId: string, ideaId: string | null) => {
    if (!ideaId) return "Entire Plan";
    const plan = brandPlans.find(p => p.id === planId);
    if (!plan) return "Deleted Post";
    
    // Handle stringified calendar if necessary
    let calendar = plan.calendar;
    if (typeof calendar === 'string') {
      try { calendar = JSON.parse(calendar); } catch { calendar = []; }
    }
    const idea = (calendar as any[]).find(i => String(i.id) === String(ideaId));
    return idea?.concept || idea?.hook || "Deleted Post";
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <MessageSquare className="w-4 h-4 text-indigo-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved': return <span className="bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full text-[10px] font-bold border border-emerald-100 uppercase tracking-wider">Approved</span>;
      case 'rejected': return <span className="bg-red-50 text-red-700 px-2.5 py-1 rounded-full text-[10px] font-bold border border-red-100 uppercase tracking-wider">Revisions Requested</span>;
      default: return <span className="bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full text-[10px] font-bold border border-indigo-100 uppercase tracking-wider">Comment</span>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">Client Review Activity</h2>
          <p className="text-sm text-gray-500">Track feedback and approvals from your shared links</p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search feedback..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-full md:w-64"
            />
          </div>
          <div className="flex bg-gray-100 p-1 rounded-xl">
             {(['all', 'approved', 'rejected', 'comment'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                    filter === t ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {t}
                </button>
             ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-3xl border border-gray-100 p-12 text-center">
          <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Fetching feedback from clients...</p>
        </div>
      ) : filteredFeedback.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100 p-16 text-center">
          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100">
            <MessageCircle className="w-8 h-8 text-gray-300" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">No activity yet</h3>
          <p className="text-gray-500 max-w-sm mx-auto mt-2">
            Feedback will appear here once you share items with clients and they submit their review.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          <AnimatePresence mode="popLayout">
            {filteredFeedback.map((f) => {
              const idea = getPostIdea(f.planId, f.ideaId);
              const planIdeas = f.ideaId ? [] : getPlanIdeas(f.planId);
              return (
                <motion.div
                  key={f.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-white rounded-2xl border border-gray-100 p-6 flex flex-col md:flex-row gap-6 hover:shadow-lg transition-all hover:border-indigo-100 group relative"
                >
                  <div className="shrink-0 flex md:flex-col items-center gap-4">
                     <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${
                       f.status === 'approved' ? 'bg-emerald-50 border-emerald-100' : 
                       f.status === 'rejected' ? 'bg-red-50 border-red-100' : 'bg-indigo-50 border-indigo-100'
                     }`}>
                       {getStatusIcon(f.status)}
                     </div>
                     <div className="h-px w-8 md:w-px md:h-8 bg-gray-100" />
                     <div className="flex flex-col items-center">
                        <p className="text-[10px] font-black uppercase text-gray-400">Time</p>
                        <p className="text-[10px] font-bold text-gray-900 whitespace-nowrap">
                          {(() => {
                            try {
                              const date = (f.createdAt as any).seconds ? new Date((f.createdAt as any).seconds * 1000) : new Date(f.createdAt);
                              return formatDistanceToNow(date, { addSuffix: true });
                            } catch (e) {
                              return 'Recently';
                            }
                          })()}
                        </p>
                     </div>
                  </div>

                  <div className="flex-1 space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200">
                          <User className="w-4 h-4 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-black text-gray-900">{f.reviewerName}</p>
                          <p className="text-xs text-gray-500">Submitted through Review Link</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(f.status)}
                        <button
                          onClick={() => f.id && handleDeleteFeedback(f.id)}
                          disabled={deletingId === f.id}
                          className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                          title="Clear/Un-approve"
                        >
                          {deletingId === f.id ? <RefreshCw className="w-4 h-4 animate-spin text-red-500" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 relative">
                      <div className="absolute -top-2 left-6 px-2 bg-indigo-600 text-white text-[8px] font-bold uppercase tracking-widest rounded-full shadow-sm">
                        Client Message
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed italic">
                        "{f.feedback || "No additional comments provided."}"
                      </p>
                    </div>

                    {/* Interactive Post Artwork & Caption Preview */}
                    {idea ? (
                      <div className="space-y-3">
                        <div className="flex justify-start">
                          <button
                            onClick={() => f.id && togglePreview(f.id)}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
                          >
                            {expandedPreviews[f.id || ''] ? (
                              <>
                                <EyeOff className="w-3.5 h-3.5" />
                                Hide Post Artwork & Caption
                              </>
                            ) : (
                              <>
                                <Eye className="w-3.5 h-3.5" />
                                Show Post Artwork & Caption
                              </>
                            )}
                          </button>
                        </div>

                        <AnimatePresence>
                          {expandedPreviews[f.id || ''] && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Caption Box */}
                                  <div className="space-y-1.5 flex flex-col">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">
                                        Generated Caption / Copy
                                      </span>
                                      <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[9px] font-black uppercase tracking-wider">
                                        {idea.format}
                                      </span>
                                    </div>
                                    <div className="flex-1 bg-white p-3 rounded-lg border border-slate-200/60 text-slate-900 text-xs whitespace-pre-wrap max-h-44 overflow-y-auto font-sans leading-relaxed shadow-inner">
                                      {idea.copy || "No caption or post content generated yet."}
                                    </div>
                                    {idea.platforms && idea.platforms.length > 0 && (
                                      <div className="flex items-center gap-1 flex-wrap mt-1">
                                        {idea.platforms.map(p => (
                                          <span key={p} className="px-2 py-0.5 rounded bg-indigo-50 text-[9px] font-black uppercase text-indigo-600 tracking-wider">
                                            {p}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  {/* Slide Visuals */}
                                  <div className="space-y-1.5">
                                    <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">
                                      Slides & Artwork Preview ({idea.slides?.length || 0})
                                    </span>
                                    {idea.slides && idea.slides.length > 0 ? (
                                      <div className="flex gap-3 overflow-x-auto pb-2 pt-1 custom-scrollbar">
                                        {idea.slides.map((slide, sIdx) => (
                                          <div key={slide.id || sIdx} className="w-28 flex-shrink-0 space-y-1.5 group/slide">
                                            <div className="w-28 h-28 rounded-lg bg-white border border-slate-200 overflow-hidden relative shadow-sm hover:shadow-md transition-all">
                                              {slide.url ? (
                                                slide.isVideo ? (
                                                  <video 
                                                    src={slide.url} 
                                                    className="w-full h-full object-cover" 
                                                    muted 
                                                    loop 
                                                    autoPlay 
                                                    playsInline
                                                  />
                                                ) : (
                                                  <img 
                                                    src={slide.url} 
                                                    alt={`Slide ${sIdx + 1}`} 
                                                    className="w-full h-full object-cover group-hover/slide:scale-105 transition-all" 
                                                    referrerPolicy="no-referrer" 
                                                  />
                                                )
                                              ) : (
                                                <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center bg-slate-50 text-[9px] text-slate-400">
                                                  <ImageIcon className="w-4 h-4 text-slate-300 mb-1" />
                                                  <span className="font-medium text-[8px] leading-tight">No Image Yet</span>
                                                </div>
                                              )}
                                              <div className="absolute bottom-1 right-1 bg-black/60 backdrop-blur-sm text-white text-[8px] font-bold px-1.5 py-0.5 rounded">
                                                Slide {sIdx + 1}
                                              </div>
                                            </div>
                                            {slide.overlayText && (
                                              <p className="text-[10px] text-slate-700 font-bold leading-snug line-clamp-2" title={slide.overlayText}>
                                                {slide.overlayText}
                                              </p>
                                            )}
                                            {slide.concept && (
                                              <p className="text-[9px] text-slate-400 italic leading-snug line-clamp-2" title={slide.concept}>
                                                {slide.concept}
                                              </p>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="bg-white rounded-lg p-6 border border-slate-200/60 text-center text-xs text-slate-400 flex flex-col items-center justify-center min-h-[112px]">
                                        <ImageIcon className="w-6 h-6 text-slate-300 mb-2" />
                                        <p className="font-semibold text-slate-500">No slides attached</p>
                                        <p className="text-[10px] text-slate-400">We'll render previews here once post visuals are generated.</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : planIdeas.length > 0 ? (
                      <div className="space-y-3">
                        <div className="flex justify-start">
                          <button
                            onClick={() => f.id && togglePreview(f.id)}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
                          >
                            {expandedPreviews[f.id || ''] ? (
                              <>
                                <EyeOff className="w-3.5 h-3.5" />
                                Hide All Plan Posts ({planIdeas.length})
                              </>
                            ) : (
                              <>
                                <Eye className="w-3.5 h-3.5" />
                                Show All Plan Posts ({planIdeas.length})
                              </>
                            )}
                          </button>
                        </div>

                        <AnimatePresence>
                          {expandedPreviews[f.id || ''] && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl space-y-4 max-h-[500px] overflow-y-auto custom-scrollbar">
                                <div className="text-xs font-black uppercase text-slate-400 tracking-wider mb-2">
                                  All Posts in this Plan
                                </div>
                                <div className="space-y-6">
                                  {planIdeas.map((pIdea, idx) => (
                                    <div key={pIdea.id || idx} className="p-4 bg-white border border-slate-100 rounded-xl shadow-sm space-y-3 text-left">
                                      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                        <div className="flex items-center gap-2">
                                          <span className="w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center text-[10px] font-black text-indigo-600">
                                            {idx + 1}
                                          </span>
                                          <span className="font-bold text-xs text-slate-800 line-clamp-1">
                                            {pIdea.concept || "Post Idea"}
                                          </span>
                                        </div>
                                        <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[9px] font-black uppercase tracking-wider">
                                          {pIdea.format}
                                        </span>
                                      </div>
                                      
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Caption Box */}
                                        <div className="space-y-1.5 flex flex-col">
                                          <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider">
                                            Caption / Copy
                                          </span>
                                          <div className="flex-1 bg-slate-50 p-2.5 rounded-lg border border-slate-200/40 text-slate-900 text-xs whitespace-pre-wrap max-h-32 overflow-y-auto font-sans leading-relaxed">
                                            {pIdea.copy || "No caption or post content."}
                                          </div>
                                        </div>

                                        {/* Slide Visuals */}
                                        <div className="space-y-1.5">
                                          <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider block">
                                            Artwork Preview ({pIdea.slides?.length || 0})
                                          </span>
                                          {pIdea.slides && pIdea.slides.length > 0 ? (
                                            <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
                                              {pIdea.slides.map((slide, sIdx) => (
                                                <div key={slide.id || sIdx} className="w-20 flex-shrink-0 space-y-1">
                                                  <div className="w-20 h-20 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden relative shadow-sm">
                                                    {slide.url ? (
                                                      slide.isVideo ? (
                                                        <video 
                                                          src={slide.url} 
                                                          className="w-full h-full object-cover" 
                                                          muted 
                                                          loop 
                                                          autoPlay 
                                                          playsInline
                                                        />
                                                      ) : (
                                                        <img 
                                                          src={slide.url} 
                                                          alt={`Slide ${sIdx + 1}`} 
                                                          className="w-full h-full object-cover" 
                                                          referrerPolicy="no-referrer" 
                                                        />
                                                      )
                                                    ) : (
                                                      <div className="w-full h-full flex flex-col items-center justify-center p-1 text-center bg-slate-100 text-[8px] text-slate-400">
                                                        <ImageIcon className="w-3.5 h-3.5 text-slate-300 mb-0.5" />
                                                        <span className="font-medium text-[7px] leading-tight">No Image</span>
                                                      </div>
                                                    )}
                                                    <div className="absolute bottom-0.5 right-0.5 bg-black/60 text-white text-[7px] font-bold px-1 py-0.2 rounded">
                                                      Slide {sIdx + 1}
                                                    </div>
                                                  </div>
                                                  {slide.overlayText && (
                                                    <p className="text-[8px] text-slate-600 font-bold leading-tight line-clamp-1" title={slide.overlayText}>
                                                      {slide.overlayText}
                                                    </p>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200/40 text-center text-[10px] text-slate-400 flex flex-col items-center justify-center min-h-[80px]">
                                              <ImageIcon className="w-4 h-4 text-slate-300 mb-1" />
                                              <span className="font-semibold text-slate-500">No slides attached</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : null}

                    <div className="flex items-center justify-between pt-2">
                      <div className="flex items-center gap-4">
                         <div className="flex items-center gap-1.5">
                           <Layers className="w-3.5 h-3.5 text-gray-400" />
                           <span className="text-[10px] font-bold text-gray-600 uppercase tracking-tight">
                             {getPostTitle(f.planId, f.ideaId)}
                           </span>
                         </div>
                         {f.ideaId && (
                           <div className="flex items-center gap-1.5">
                             <Calendar className="w-3.5 h-3.5 text-gray-400" />
                             <span className="text-[10px] font-bold text-gray-600 uppercase tracking-tight">
                               Specific Post Feedback
                             </span>
                           </div>
                         )}
                      </div>

                      <button 
                        onClick={() => onViewInCalendar(f.planId, f.ideaId)}
                        className="flex items-center gap-1.5 text-[10px] font-black uppercase text-indigo-600 hover:text-indigo-700 tracking-widest group"
                      >
                        View in Calendar
                        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
